// src/utils/mockDataWorkInstructions.ts
// Work Instructions module — SOP library and job-specific work packages
// Briefing: TrafficProcure Work Instructions Module — June 2026

// ── Types ─────────────────────────────────────────────────────────

export type WIType       = 'SOP' | 'JOB_PACKAGE'
export type WIDiscipline = 'CIVIL' | 'ELECTRICAL' | 'HV_ELECTRICAL' | 'TRAFFIC_CONTROL' | 'COMMISSIONING' | 'ALL' | 'OTHER'
export type WIStatus     = 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'DEPLOYED' | 'IN_PROGRESS' | 'SUBMITTED' | 'REWORK_REQUIRED' | 'COMPLETE' | 'SUPERSEDED' | 'CANCELLED'

export interface WIStep {
  number: number
  title: string
  instruction: string
  isHoldPoint: boolean
  holdPointRef?: string
  holdPointDescription?: string
  requiresPhoto: boolean
  photoCount?: number
  photoRequirement?: string
  qualityCheck?: string
  asBuiltField?: string
  estimatedHours?: number        // labour hours for this step (0 for hold points)
  tradeCode?: string             // TradeCode key — matches TRADE_RATE_MAP in mockDataClientSpecs
  completedAt?: string | null
  completedById?: string | null
}

export interface WorkInstruction {
  id: string
  reference: string
  title: string
  version: string
  type: WIType
  discipline: WIDiscipline
  status: WIStatus
  // Job package fields
  applicableJobIds?: string[]
  // Phase 1: real FK to DrawingTask (replaces free-text taskReference)
  drawingTaskId?: string | null       // DrawingTask.id — resolves via workflowPipeline.ts
  taskReference?: string              // kept for legacy display only
  drawingReference?: string
  specificationClause?: string
  siteSpecificNotes?: string
  baseSopId?: string
  // Regulatory
  applicableRegulations: string[]
  requiredCredentials: string[]
  requiredTools: string[]
  ppeRequirements: string[]
  swmsReference?: string
  // Client specification links
  specReferences?: { specId: string; clauses: string[] }[]
  // Content
  steps: WIStep[]
  estimatedDuration: string
  // Workflow
  authorisedById?: string | null
  authorisedByName?: string | null
  authorisedAt?: string | null
  assignedToId?: string | null
  assignedToName?: string | null
  deployedAt?: string | null
  completedById?: string | null
  completedByName?: string | null
  completedAt?: string | null
  reworkNote?: string | null
  // Meta
  createdById: string
  createdByName: string
  createdAt: string
  updatedAt: string
  notes?: string
}

// ── Display metadata ───────────────────────────────────────────────

export const DISCIPLINE_META: Record<WIDiscipline, { label: string; bg: string; text: string; dot: string }> = {
  CIVIL:           { label: 'Civil',           bg: 'bg-teal-100',   text: 'text-teal-800',   dot: 'bg-teal-500' },
  ELECTRICAL:      { label: 'Electrical',      bg: 'bg-blue-100',   text: 'text-blue-800',   dot: 'bg-blue-500' },
  HV_ELECTRICAL:   { label: 'HV Electrical',   bg: 'bg-red-100',    text: 'text-red-800',    dot: 'bg-red-500' },
  TRAFFIC_CONTROL: { label: 'Traffic Control', bg: 'bg-amber-100',  text: 'text-amber-800',  dot: 'bg-amber-500' },
  COMMISSIONING:   { label: 'Commissioning',   bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  ALL:             { label: 'All disciplines', bg: 'bg-gray-100',   text: 'text-gray-700',   dot: 'bg-gray-400' },
  OTHER:           { label: 'Other',           bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-300' },
}

export const WI_STATUS_META: Record<WIStatus, { label: string; bg: string; text: string }> = {
  DRAFT:           { label: 'Draft',                  bg: 'bg-gray-100',   text: 'text-gray-600' },
  PENDING_REVIEW:  { label: 'Pending review',         bg: 'bg-amber-100',  text: 'text-amber-700' },
  APPROVED:        { label: 'Approved',               bg: 'bg-blue-100',   text: 'text-blue-700' },
  DEPLOYED:        { label: 'Deployed',               bg: 'bg-purple-100', text: 'text-purple-700' },
  IN_PROGRESS:     { label: 'In progress',            bg: 'bg-sky-100',    text: 'text-sky-700' },
  SUBMITTED:       { label: 'Pending PM sign-off',    bg: 'bg-teal-100',   text: 'text-teal-700' },
  REWORK_REQUIRED: { label: 'Rework required',        bg: 'bg-red-100',    text: 'text-red-700' },
  COMPLETE:        { label: 'Complete',               bg: 'bg-green-100',  text: 'text-green-700' },
  SUPERSEDED:      { label: 'Superseded',             bg: 'bg-gray-100',   text: 'text-gray-400' },
  CANCELLED:       { label: 'Cancelled',              bg: 'bg-gray-100',   text: 'text-gray-400' },
}

// ── SOP Library ───────────────────────────────────────────────────

export const MOCK_SOPS: WorkInstruction[] = [
  {
    id: 'wi-sop-001',
    reference: 'WI-SOP-CIV-001',
    title: 'Signal pole foundation — install and backfill',
    version: '1.2',
    type: 'SOP',
    discipline: 'CIVIL',
    status: 'APPROVED',
    applicableRegulations: ['MRTS50 cl. 6.3.4', 'AS 3600 — Concrete structures'],
    requiredCredentials: ['Construction Induction (White Card)', 'Excavation spotter (or licensed excavator operator)'],
    requiredTools: ['Excavator (300mm bucket)', 'Spirit level (1.2m min)', 'Survey tripod & laser level', 'Vibrating plate compactor', 'Nuclear density gauge'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Gloves'],
    specReferences: [{ specId: 'spec-mrts92', clauses: ['cl. 4.3', 'cl. 5.1'] }, { specId: 'spec-mrts91', clauses: ['cl. 3.2'] }],
    steps: [
      {
        number: 1,
        title: 'Confirm location and services check',
        instruction: 'Verify survey pegs are in correct position per approved drawings. Confirm DBYD clearance is current. Use CAT scanner to sweep excavation area. Do not excavate within 500mm of any identified service without PM instruction.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1,
        photoRequirement: 'Existing surface at foundation location — survey peg and CAT scanner visible',
        estimatedHours: 0.5, tradeCode: 'CL2',
      },
      {
        number: 2,
        title: 'Excavate to specified depth and diameter',
        instruction: 'Excavate to depth and diameter specified on the engineering drawing for this pole type. Minimum 900mm depth for standard 8m pole. Check walls are clean and vertical. Remove all loose material from base.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Tape measure showing depth; excavation walls and base from above',
        estimatedHours: 1.0, tradeCode: 'CL1',
      },
      {
        number: 3,
        title: 'HOLD POINT — PC inspection at depth',
        instruction: 'STOP. Do not proceed until PC or PM has physically inspected and confirmed excavation depth and diameter comply with the engineering drawing. Record inspector name and time on this work instruction.',
        isHoldPoint: true, holdPointRef: 'HP-001',
        holdPointDescription: 'Excavation depth and diameter verification before base plate installation',
        requiresPhoto: false, estimatedHours: 0, tradeCode: 'SV',
      },
      {
        number: 4,
        title: 'Install base plate and set to level',
        instruction: 'Position DIN base plate in excavation. Align bolt orientation per drawing. Level plate in both axes — tolerance ±5mm per metre. Secure temporarily with timber shims if required.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Base plate in position with spirit level resting on plate; orientation reference visible',
        qualityCheck: 'Level must read plumb in both axes. Orientation must match drawing.',
        estimatedHours: 1.0, tradeCode: 'CL2',
      },
      {
        number: 5,
        title: 'Record concrete batch details before pour',
        instruction: 'Confirm delivery docket matches specification: Grade 40 minimum, 20mm aggregate, slump 70–100mm. Record batch number, truck number and delivery time. Do not accept non-conforming concrete.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1,
        photoRequirement: 'Concrete delivery docket showing batch number and mix specification',
        asBuiltField: 'Batch no: ___  Truck no: ___  Delivery time: ___',
        estimatedHours: 0.25, tradeCode: 'CL2',
      },
      {
        number: 6,
        title: 'Pour and finish Grade 40 concrete',
        instruction: 'Pour concrete in layers — do not drop from height >1m. Do not vibrate within 200mm of base plate. Fill to ground level on all sides. Check bolt projection and level after pour — adjust before initial set only.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Concrete being placed (base plate visible at level); completed pour with bolt projection (tape measure visible)',
        qualityCheck: 'Bolt projection matches drawing ±5mm. Level within tolerance at completion.',
        asBuiltField: 'Bolt projection (mm): ___  Level check result: ___',
        estimatedHours: 0.5, tradeCode: 'CL1',
      },
      {
        number: 7,
        title: 'Backfill and compact in lifts',
        instruction: 'After minimum 4hr initial set, backfill perimeter in 200mm lifts. Compact each lift to ≥95% Standard Proctor using vibrating plate. Use hand tamper within 300mm of concrete. Do not compact against fresh concrete.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'Minimum 95% Standard Proctor per lift — record nuclear density gauge result',
        asBuiltField: 'Compaction L1: ___% L2: ___% L3: ___%',
        estimatedHours: 1.5, tradeCode: 'CL1',
      },
      {
        number: 8,
        title: 'Final inspection and as-built completion',
        instruction: 'Inspect completed foundation. Cap bolts. Confirm concrete flush with surrounding surface per drawing. Reinstate disturbed pavement or turf to match surroundings. Complete all as-built fields.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Foundation from above showing bolt layout; reinstated surface condition',
        estimatedHours: 0.25, tradeCode: 'CL2',
      },
    ],
    estimatedDuration: '1 day',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-01T10:00:00Z',
    createdAt: '2026-02-20T09:00:00Z', updatedAt: '2026-03-01T10:00:00Z',
  },

  {
    id: 'wi-sop-002',
    reference: 'WI-SOP-CIV-002',
    title: 'UPVC cable pit — set and seal',
    version: '1.0',
    type: 'SOP',
    discipline: 'CIVIL',
    status: 'APPROVED',
    applicableRegulations: ['MRTS50 cl. 7.1', 'AS/NZS 3000 (conduit entry requirements)'],
    requiredCredentials: ['Construction Induction (White Card)'],
    requiredTools: ['Excavator (300mm bucket)', 'Laser level', 'Hand tamper', 'Silicone sealant gun'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses'],
    steps: [
      {
        number: 1, title: 'Confirm pit location and services clearance',
        instruction: 'Verify pit location against design drawings. Confirm DBYD clearance and CAT sweep. Mark exact position with survey spray paint.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Marked pit location at ground level',
      },
      {
        number: 2, title: 'Excavate pit opening',
        instruction: 'Excavate pit opening 100mm larger than pit outside dimensions on all sides to allow for bedding and alignment adjustment. Excavate to specified base depth. Remove all loose material.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Excavation at correct depth — tape measure visible',
      },
      {
        number: 3, title: 'Set 100mm gravel bedding layer',
        instruction: 'Place and compact 100mm gravel bedding layer to correct level. Confirm level with laser — tolerance ±10mm. Bedding must be free from clay or organic material.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'Bedding level within ±10mm of design level',
      },
      {
        number: 4, title: 'Set pit to level and orientation',
        instruction: 'Lower pit into position. Align conduit entry knockouts to match design conduit directions. Level pit in both axes — maximum 5mm in length of pit. Backfill and compact to stabilise.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1,
        photoRequirement: 'Pit set at level with spirit level on lid frame visible',
        qualityCheck: 'Level within 5mm. Conduit knockouts aligned to drawing.',
      },
      {
        number: 5, title: 'Install conduit entries and seal',
        instruction: 'Install conduit entries through pit knockouts using correct entry adaptors. Apply silicone sealant around all conduit entries — minimum 10mm bead. Apply sealant to all unused knockouts. Allow to cure before pit is subjected to inundation.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Conduit entries with sealant applied (all openings); completed pit interior from above',
      },
      {
        number: 6, title: 'Backfill, compact and reinstate',
        instruction: 'Backfill remaining void around pit in 200mm lifts, compact to ≥95% Standard Proctor. Reinstate pavement or surface to match. Confirm pit lid is correctly seated and locked.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Completed and reinstated pit with lid seated and locked',
        qualityCheck: 'Lid must be flush ±5mm with surrounding surface.',
      },
    ],
    estimatedDuration: '4–6 hours',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-05T14:00:00Z',
    createdAt: '2026-03-01T11:00:00Z', updatedAt: '2026-03-05T14:00:00Z',
  },

  {
    id: 'wi-sop-003',
    reference: 'WI-SOP-ELEC-001',
    title: 'Multi-core cable pulling — conduit installation',
    version: '2.0',
    type: 'SOP',
    discipline: 'ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['AS/NZS 3000 — Wiring Rules', 'Energex Cable Installation Guideline ECI-004'],
    requiredCredentials: ['Electrical licence (LL or above)', 'Construction Induction (White Card)'],
    requiredTools: ['Cable pulling winch', 'Draw wire (pre-installed)', 'Cable drum stand with brake', 'Cable tension gauge', 'Fish tape'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Gloves (cut-resistant)'],
    specReferences: [{ specId: 'spec-mrts256', clauses: ['cl. 3.4', 'cl. 5.2'] }, { specId: 'spec-as3000', clauses: ['cl. 3.4'] }],
    steps: [
      {
        number: 1, title: 'Record cable drum details',
        instruction: 'Before breaking drum seal, photograph drum label showing manufacturer, batch number, cable type, cross-section, and AS/NZS certification mark. Record details on this work instruction.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1,
        photoRequirement: 'Cable drum label — batch number and AS/NZS certification mark must be legible',
        asBuiltField: 'Cable type: ___  Batch no: ___  Drum no: ___',
        estimatedHours: 0.25, tradeCode: 'EL2',
      },
      {
        number: 2, title: 'Inspect conduit run before pulling',
        instruction: 'Draw fish tape through conduit run from pull pit to pull pit. Confirm conduit is continuous and unobstructed. If resistance is felt, report to PM — do not force. Confirm conduit fill ratio will not be exceeded by the proposed cables.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'AS/NZS 3000 conduit fill ratio must not exceed 40% for multi-cable pulls.',
        estimatedHours: 0.5, tradeCode: 'EL2',
      },
      {
        number: 3, title: 'Set up drum stand and pulling rig',
        instruction: 'Mount cable drum on stand with brake engaged. Position pulling winch at receiving pit. Attach pulling rope to cable using correct cable grip (not knot). Confirm all personnel are clear of the cable path before commencing.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 4, title: 'Pull cable — monitor tension',
        instruction: 'Pull cable at steady speed — do not exceed manufacturer maximum pulling tension. Use tension gauge if long run (>50m) or multiple bends. Cease pulling immediately if cable bunches or tension spikes. Record maximum tension reached.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Cable pulling in progress — cable visible entering conduit; completed pull showing cable at receiving end',
        asBuiltField: 'Max pull tension recorded: ___ kN  Run length: ___ m',
      },
      {
        number: 5, title: 'Label and secure cable ends',
        instruction: 'Label both ends of each cable with circuit reference per drawing. Coil and secure cable ends in pit — minimum 1m service loop. Seal conduit entries at both ends with conduit sealant plug. Photograph completed pull.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2,
        photoRequirement: 'Labelled cable ends at each pit showing circuit ID; sealed conduit entries',
      },
    ],
    estimatedDuration: '2–4 hours per run',
    createdById: 'u6', createdByName: 'Mike Walsh',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-04-10T09:00:00Z',
    createdAt: '2026-04-05T08:30:00Z', updatedAt: '2026-04-10T09:00:00Z',
  },

  {
    id: 'wi-sop-004',
    reference: 'WI-SOP-ELEC-002',
    title: 'LED signal head — mount and align',
    version: '1.1',
    type: 'SOP',
    discipline: 'ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['QD-TSG-0026 Rev B cl. 5.1', 'AS/NZS 4191 — Signal lanterns'],
    requiredCredentials: ['Electrical licence (LL or above)', 'EWP operator (if mounting above 4m)', 'Working at heights (>2m)'],
    requiredTools: ['EWP (if required)', 'Torque wrench (calibrated)', 'Inclinometer', 'Cable pull-through tool'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Full harness (if EWP)'],
    steps: [
      {
        number: 1, title: 'Confirm signal head type and orientation',
        instruction: 'Verify signal head model matches specification (2-aspect / 3-aspect / pedestrian per drawing). Confirm facing direction per drawing compass orientation. Photograph installation point before attachment.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Signal head at ground level before mounting — type label visible',
      },
      {
        number: 2, title: 'Mount signal head bracket to pole',
        instruction: 'Attach bracket at height specified per drawing. Torque bracket bolts to manufacturer specification. Record torque value. Apply anti-corrosion compound to all stainless fasteners.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'During mounting — bracket installed on pole at correct height',
        qualityCheck: 'Torque value per manufacturer data sheet. Record actual torque applied.',
        asBuiltField: 'Bolt torque applied (Nm): ___  Height from finished ground (mm): ___',
      },
      {
        number: 3, title: 'Hang and align signal head',
        instruction: 'Hang signal head on bracket. Align vertically (plumb) and set horizontal aim per drawing — nominally aimed at stop line. Use inclinometer to confirm tilt within ±0.5°. Secure all locking fasteners.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Completed mounting — signal head at operating height, facing direction visible',
        qualityCheck: 'Tilt within ±0.5° from vertical. Aim direction matches drawing.',
      },
      {
        number: 4, title: 'Terminate cable and test',
        instruction: 'Route cables through pole and into signal head compartment. Terminate per wiring diagram. Test each LED circuit prior to commissioning. Confirm no shorts and correct phase assignment.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'Continuity and IR test each circuit before energisation. No shorts to earth.',
      },
    ],
    estimatedDuration: '1–2 hours per head',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-15T11:00:00Z',
    createdAt: '2026-03-10T10:00:00Z', updatedAt: '2026-03-15T11:00:00Z',
  },

  {
    id: 'wi-sop-005',
    reference: 'WI-SOP-ELEC-003',
    title: 'SCATS controller — cabinet fit-out and firmware check',
    version: '1.0',
    type: 'SOP',
    discipline: 'ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['QD-TSG-0026 cl. 4.2', 'TMR Traffic Signal Controller Spec Rev 5'],
    requiredCredentials: ['Electrical licence (EL1 or above)', 'SCATS Systems Access accreditation (TMR)'],
    requiredTools: ['Calibrated multimeter', 'Laptop with SCATS configuration software', 'Serial/USB interface cable'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Insulated gloves (for LV work)'],
    steps: [
      {
        number: 1, title: 'Photograph controller before fit-out',
        instruction: 'Photograph cabinet interior and all factory-installed components before commencing field wiring. Record firmware version displayed on controller head. Photograph controller serial number and model label.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Cabinet interior (before); controller model plate and serial number',
        asBuiltField: 'Controller serial no: ___  Firmware version: ___  Cabinet model: ___',
      },
      {
        number: 2, title: 'Terminate field wiring per schedule',
        instruction: 'Terminate all field cables at terminal blocks per the wiring schedule. Label each cable at both ends with circuit reference. Verify terminal torque settings per manufacturer spec. No bare conductor to be exposed beyond terminal ferrule.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'All cables labelled. No exposed conductors. Torque per spec.',
      },
      {
        number: 3, title: 'HOLD POINT — wiring check before energisation',
        instruction: 'STOP. PM or licensed electrician (EL2) to verify wiring schedule compliance before the cabinet is energised. Check for correct phase assignment, grounding, and no shorts between phases.',
        isHoldPoint: true, holdPointRef: 'HP-ELEC-01', holdPointDescription: 'Pre-energisation wiring verification by EL2 or above',
        requiresPhoto: false,
      },
      {
        number: 4, title: 'Load and verify SCATS configuration file',
        instruction: 'Connect laptop to controller. Load job-specific configuration file (provided by PM/TMR) via SCATS software. Verify all intersection parameters match the configuration schedule. Confirm correct SCATS ID has been assigned.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'SCATS software screen showing loaded configuration and intersection ID',
        asBuiltField: 'SCATS intersection ID: ___  Config file version: ___  Loaded by: ___',
      },
      {
        number: 5, title: 'Completed fit-out photo record',
        instruction: 'Photograph completed cabinet interior showing all wiring dressed and labelled. Photograph exterior cabinet with door closed and sealed. Record completion time.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Completed interior with cable labels visible; exterior cabinet with door closed',
      },
    ],
    estimatedDuration: '4–6 hours',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-04-01T09:00:00Z',
    createdAt: '2026-03-25T10:00:00Z', updatedAt: '2026-04-01T09:00:00Z',
  },

  {
    id: 'wi-sop-006',
    reference: 'WI-SOP-ELEC-004',
    title: 'Cable termination — 3C+E 6mm² (low voltage)',
    version: '1.3',
    type: 'SOP',
    discipline: 'ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['AS/NZS 3000 — Wiring Rules', 'AS/NZS 3008.1.1 — Cable selection'],
    requiredCredentials: ['Electrical licence (EL1 or above)'],
    requiredTools: ['Cable stripper (correct sizing)', 'Ferrule crimper', 'Calibrated torque screwdriver', 'Insulation resistance tester'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Insulated gloves'],
    steps: [
      {
        number: 1, title: 'Identify and label cable',
        instruction: 'Confirm cable circuit reference matches wiring schedule. Apply cable label at both ends before commencing termination. Measure and record cable sheath insulation resistance before stripping.',
        isHoldPoint: false, requiresPhoto: false,
        asBuiltField: 'Circuit ref: ___  IR test result (MΩ): ___  Test voltage: ___',
      },
      {
        number: 2, title: 'Strip, ferrule, and terminate',
        instruction: 'Strip outer sheath to correct length. Ferrule all conductors with correct ferrule size for terminal type. Terminate in correct phase order (A=Red, B=White, C=Blue, E=Green/Yellow). Torque per terminal manufacturer specification.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'No exposed conductor beyond ferrule. Correct phase colour. Torque per spec.',
      },
      {
        number: 3, title: 'Post-termination IR test',
        instruction: 'After termination, conduct insulation resistance test at 500V DC between each conductor and earth. Record results. All readings must be >100MΩ before energisation.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'IR tester display showing result — circuit ID must be visible in background',
        qualityCheck: 'All IR readings >100MΩ at 500V DC.',
        asBuiltField: 'IR A-E: ___ MΩ  IR B-E: ___ MΩ  IR C-E: ___ MΩ',
      },
    ],
    estimatedDuration: '30–60 min per circuit',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-20T14:00:00Z',
    createdAt: '2026-03-18T09:00:00Z', updatedAt: '2026-03-20T14:00:00Z',
  },

  {
    id: 'wi-sop-007',
    reference: 'WI-SOP-HV-001',
    title: '33kV XLPE cable joint — heat shrink method',
    version: '2.1',
    type: 'SOP',
    discipline: 'HV_ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['Energex TEC-001 Rev 4', 'AS/NZS 1429.1 — Power cables', 'Energex HV Switching Code'],
    requiredCredentials: ['HV authorisation (Energex AP holder)', 'Electrical licence (EL2 or above)', 'HV cable jointing endorsement'],
    requiredTools: ['HV jointing kit (manufacturer-matched)', 'Heat gun (calibrated temp range)', 'HV test set', 'Partial discharge analyser'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'HV rated gloves (Class 2)', 'Arc flash PPE'],
    swmsReference: 'SWMS-HV-JOINTING-001',
    steps: [
      {
        number: 1, title: 'Confirm switching programme — obtain clearance',
        instruction: 'Confirm HV Switching Programme (HVSP) is completed and authorised. Obtain point of isolation clearance from Energex System Control. Do not commence jointing without HVSP clearance in hand. Record clearance number.',
        isHoldPoint: true, holdPointRef: 'HP-HV-01', holdPointDescription: 'HV clearance confirmation from Energex System Control before work commences',
        requiresPhoto: false,
        asBuiltField: 'HVSP clearance number: ___  Issued by: ___  Time: ___',
      },
      {
        number: 2, title: 'Confirm cable dead — earthing applied',
        instruction: 'Use approved HV voltage detector to confirm cable is dead at both jointing ends. Apply earth clamps at both ends of the cable section. Do not remove earth clamps until work is fully complete and HVSP returned.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Earthing clamps applied at each cable end; HV detector being used on cable',
      },
      {
        number: 3, title: 'Prepare cable ends',
        instruction: 'Cut cable ends square — minimum 500mm past any damaged sheath. Strip sheath, insulation screen, and XLPE insulation to manufacturer joint kit dimensions. Handle exposed XLPE insulation only with clean cotton gloves — no skin contact.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'All dimensions within joint kit manufacturer tolerances. Insulation surface free from contamination.',
      },
      {
        number: 4, title: 'Apply joint kit — heat shrink method',
        instruction: 'Apply all components of the approved joint kit strictly per manufacturer installation instructions. Control heat application using temperature probe — do not overheat. Sequential application of stress cones, insulation rebuild, and outer sheath restoration.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 3, photoRequirement: 'Each major stage: stress cone applied; insulation rebuild; completed joint before outer sheath',
      },
      {
        number: 5, title: 'HOLD POINT — joint inspection before reinstatement',
        instruction: 'STOP. PM or senior jointer to inspect completed joint before outer sheath application. Inspect stress cones, insulation rebuild and mechanical connector for correct assembly. Record inspector name and time.',
        isHoldPoint: true, holdPointRef: 'HP-HV-02', holdPointDescription: 'Joint quality inspection by PM or accredited jointer before sheath reinstatement',
        requiresPhoto: false,
      },
      {
        number: 6, title: 'Post-joint HV test — DC or VLF',
        instruction: 'After joint curing time (min 1hr from heat completion), conduct HV test per Energex specification. Apply DC or VLF test voltage per cable rating. Record test result and PASS/FAIL. Joint MUST pass before HVSP is returned.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'HV test set display showing applied voltage and result — joint visible in background',
        asBuiltField: 'Test method: ___  Test voltage: ___ kV  Duration: ___ min  Result: PASS / FAIL',
      },
    ],
    estimatedDuration: '1 day (2-person crew)',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-02-15T12:00:00Z',
    createdAt: '2026-02-01T09:00:00Z', updatedAt: '2026-02-15T12:00:00Z',
  },

  {
    id: 'wi-sop-008',
    reference: 'WI-SOP-TC-001',
    title: 'Traffic controller setup — single-lane alternating',
    version: '1.0',
    type: 'SOP',
    discipline: 'TRAFFIC_CONTROL',
    status: 'APPROVED',
    applicableRegulations: ['AS 1742.3 — Traffic control devices', 'MUTCD Part 3 — Works zones', 'TMR TMAP Requirements'],
    requiredCredentials: ['Traffic Controller licence (Queensland)', 'Traffic Management Implementer (if supervisor)'],
    requiredTools: ['Stop/Slow bats', 'Radio (2 controllers)', 'Signing trailer or portable signs', 'Delineator cones or wands'],
    ppeRequirements: ['Hi-vis (Class D — mandatory for TCP)', 'Hard hat (site dependent)', 'Steel-capped boots'],
    steps: [
      {
        number: 1, title: 'Confirm TMP document before setup',
        instruction: 'Locate and read the approved Traffic Management Plan (TMP) for this job/site. Confirm TMP matches site conditions — road width, speed zone, time of day, and hazard type. Do not implement any TCP arrangement not covered by the current approved TMP.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'TMP must be current revision. Site conditions must match TMP assumptions.',
      },
      {
        number: 2, title: 'Set advance warning signage',
        instruction: 'Erect advance warning signs per TMP layout diagram. Minimum taper length per AS 1742.3 table. Signs must be visible at the required sight distance (speed-dependent). Photograph signage in place before TCP begins directing traffic.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Advance warning signage at both approaches, showing sight distance; taper delineators in place',
      },
      {
        number: 3, title: 'Position traffic controllers',
        instruction: 'Controllers position at the STOP points shown on TMP. Each controller must have radio contact confirmed with opposite controller before allowing any vehicles to enter the single-lane section. Confirm queue length is manageable before opening.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'TCP in position at stop point with signage visible behind — site condition before first vehicle release',
      },
      {
        number: 4, title: 'Reinstatement and sign removal',
        instruction: 'Once works are complete for the day or session, remove delineators and signs in the correct sequence (start at far end, work toward). Confirm road is fully reopened and no equipment remains on carriageway before logging off.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Road reinstated to normal traffic — all signs and delineators removed, site condition on departure',
      },
    ],
    estimatedDuration: 'Duration of works (daily)',
    createdById: 'u6', createdByName: 'Mike Walsh',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-10T11:00:00Z',
    createdAt: '2026-03-05T09:00:00Z', updatedAt: '2026-03-10T11:00:00Z',
  },

  {
    id: 'wi-sop-009',
    reference: 'WI-SOP-ALL-001',
    title: 'Confined space entry — cable pit (Category 3)',
    version: '1.1',
    type: 'SOP',
    discipline: 'ALL',
    status: 'APPROVED',
    applicableRegulations: ['WHS Regulation 2011 Part 6.5 — Confined spaces', 'Code of Practice: Confined Spaces (Safe Work Australia)', 'AS 2865 — Safe working in confined spaces'],
    requiredCredentials: ['Confined Space Entry (worker)', 'Confined Space Entry Supervisor', 'Rescue team trained (standby person)'],
    requiredTools: ['Atmospheric test instrument (O2, LEL, CO, H2S)', 'Tripod and rescue winch', 'SCBA or supplied air (if atmosphere requires)', 'Full body harness and rescue lanyard'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Full body harness', 'Chemical-resistant gloves'],
    swmsReference: 'SWMS-CONFINED-001',
    steps: [
      {
        number: 1, title: 'Complete confined space entry permit',
        instruction: 'Supervisor completes Form WHSO-CS-001 — Confined Space Entry Permit before any entry. Confirm works scope, potential hazards, isolation requirements, and rescue plan. All entrants must sign the permit before entering.',
        isHoldPoint: false, requiresPhoto: false,
        asBuiltField: 'Permit number: ___  Supervisor: ___  Time opened: ___',
      },
      {
        number: 2, title: 'Conduct atmospheric test — all four gases',
        instruction: 'Use calibrated 4-gas instrument to test atmosphere at three levels (top, mid, bottom) of the pit. Record all readings. Entry is PROHIBITED if: O2 < 19.5% or > 23.5%; LEL > 5%; CO > 25ppm; H2S > 10ppm.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Atmospheric test instrument display showing all four readings with pit visible',
        qualityCheck: 'O2: 19.5–23.5%. LEL: <5%. CO: <25ppm. H2S: <10ppm.',
        asBuiltField: 'O2: ___% LEL: ___% CO: ___ppm H2S: ___ppm  Instrument cal date: ___',
      },
      {
        number: 3, title: 'HOLD POINT — atmosphere confirmed safe, supervisor sign-off',
        instruction: 'STOP. Supervisor must review and sign atmospheric test results before entry is permitted. If any reading is out of range, isolate source, ventilate, and re-test. Do not proceed without supervisor written sign-off on the entry permit.',
        isHoldPoint: true, holdPointRef: 'HP-CS-01', holdPointDescription: 'Atmospheric test sign-off by confined space supervisor',
        requiresPhoto: false,
      },
      {
        number: 4, title: 'Set up tripod and rescue equipment',
        instruction: 'Erect tripod directly over pit opening. Attach rescue winch and lanyard. Confirm standby person is in position and has unobstructed view of the entrant or radio contact. Confirm emergency contact number and nearest first aid kit are identified.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Tripod in position over pit opening; standby person visible',
      },
      {
        number: 5, title: 'Entry — harness and rescue lanyard',
        instruction: 'Entrant dons full body harness. Attach rescue lanyard to tripod winch before entering. Standby person maintains constant contact (radio or visual). Entrant enters pit — do NOT enter head-first.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 6, title: 'Conduct works task',
        instruction: 'Conduct the specified works task per the relevant work instruction for that task. Monitor atmosphere continuously throughout entry — re-test every 30 minutes or immediately if any odour, symptom, or alarm occurs. Evacuate immediately if atmosphere alarm sounds.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 7, title: 'Exit and close out permit',
        instruction: 'All entrants exit pit and tally count is confirmed. Replace pit lid. Supervisor closes out entry permit — records actual exit time. Retain completed permit on job file for 12 months.',
        isHoldPoint: false, requiresPhoto: false,
        asBuiltField: 'Exit time: ___  All persons accounted for: YES / NO  Permit closed by: ___',
      },
    ],
    estimatedDuration: '2 hours minimum (including setup)',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-02-10T14:00:00Z',
    createdAt: '2026-02-05T10:00:00Z', updatedAt: '2026-02-10T14:00:00Z',
  },

  {
    id: 'wi-sop-010',
    reference: 'WI-SOP-ALL-002',
    title: 'EWP pre-start check and elevated work setup',
    version: '1.0',
    type: 'SOP',
    discipline: 'ALL',
    status: 'APPROVED',
    applicableRegulations: ['AS 2550.10 — EWP operation', 'WHS Regulations 2011 — Working at heights', 'Code of Practice: Managing the risk of falls at workplaces'],
    requiredCredentials: ['EWP operator licence (relevant class)', 'Working at heights competency'],
    requiredTools: ['EWP (pre-start checked)', 'Full body harness (Class A)', 'Lanyard (single or twin-leg)', 'Spotters'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Full body harness', 'Gloves'],
    steps: [
      {
        number: 1, title: 'EWP pre-start inspection',
        instruction: 'Complete the manufacturer pre-start checklist for the EWP model. Check oil, water, tyres/tracks, controls, alarms, and basket condition. Confirm valid service/maintenance tag is fitted. Do not operate an EWP with a deficiency that affects safety.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'EWP pre-start tag and machine condition — defect-free confirmation',
        asBuiltField: 'EWP fleet no: ___  Pre-start result: PASS / FAIL  Defects noted: ___',
      },
      {
        number: 2, title: 'Assess ground conditions and set-up area',
        instruction: 'Assess ground conditions for level, stability, and overhead hazards. Identify underground services below outrigger positions. Set outriggers on timber mats if required. Confirm overhead clearance — minimum 3m from powerlines unless isolated.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'Maximum slope per manufacturer specs. Outrigger positions clear of services. Powerline clearance confirmed.',
      },
      {
        number: 3, title: 'Harness inspection and fit',
        instruction: 'Inspect full body harness for damage, wear, and correct fit before use. Connect lanyard to basket anchor point before entering basket. Do not connect to non-approved anchor points. Confirm lanyard length does not allow fall beyond basket.',
        isHoldPoint: false, requiresPhoto: false,
        qualityCheck: 'Harness free of cuts, excessive wear, or chemical contamination. Lanyard anchor point is rated.',
      },
      {
        number: 4, title: 'Elevate and complete works',
        instruction: 'Operate EWP within rated capacity at all times. Do not reach outside basket. Do not transfer between EWP and structure at height unless specific task instruction exists for this. Confirm spotter is in place at ground level for the duration.',
        isHoldPoint: false, requiresPhoto: false,
      },
    ],
    estimatedDuration: '1 hour (setup) + task duration',
    createdById: 'u6', createdByName: 'Mike Walsh',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-03-22T10:00:00Z',
    createdAt: '2026-03-20T09:00:00Z', updatedAt: '2026-03-22T10:00:00Z',
  },

  {
    id: 'wi-sop-011',
    reference: 'WI-SOP-COM-001',
    title: 'ITS system functional test — SCATS commissioning',
    version: '1.0',
    type: 'SOP',
    discipline: 'COMMISSIONING',
    status: 'APPROVED',
    applicableRegulations: ['QD-TSG-0026 Rev B cl. 8', 'TMR ITS Commissioning Requirements — Signal Systems', 'SCATS Network Operations Manual'],
    requiredCredentials: ['SCATS Systems Access accreditation (TMR)', 'Electrical licence (EL1 or above)'],
    requiredTools: ['Laptop with SCATS software and TMR authorised access', 'Calibrated multimeter', 'Site radio (TMR control room contact)'],
    ppeRequirements: ['Hi-vis Class D', 'Hard hat (on-site portions)', 'Steel-capped boots'],
    steps: [
      {
        number: 1, title: 'Confirm commissioning authority from TMR',
        instruction: 'Confirm written commissioning authority has been received from TMR prior to any live SCATS system testing. Commissioning must not proceed without written authority.',
        isHoldPoint: true, holdPointRef: 'HP-COM-01', holdPointDescription: 'TMR written commissioning authority confirmation',
        requiresPhoto: false,
        asBuiltField: 'TMR authority reference: ___  Issued by: ___  Date: ___',
      },
      {
        number: 2, title: 'Phase and signal head operation check',
        instruction: 'Manually step through all signal phases using controller local panel. Confirm correct head illumination for each phase. Confirm pedestrian phases operate and interlock correctly. Record any non-conformances.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Signal heads cycling — each major phase showing correct aspect illumination',
        qualityCheck: 'All phases operate as per phase diagram. No wrong-way greens. Pedestrian interlocks correct.',
        asBuiltField: 'Non-conformances: ___',
      },
      {
        number: 3, title: 'SCATS communication test — controller to regional server',
        instruction: 'Confirm SCATS communication link is active between intersection controller and TMR regional server. Log communication status from SCATS software. Confirm data is being received at TMR end (confirm with TMR control room operator by phone/radio).',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'SCATS software screen showing active communication link to regional server',
        asBuiltField: 'Communication status: ___  TMR operator confirmed: ___  Time: ___',
      },
      {
        number: 4, title: 'Final sign-off — TMR inspector',
        instruction: 'TMR inspector to witness functional test and sign commissioning record. PM to countersign. Forward signed record to TMR and retain copy in job file.',
        isHoldPoint: true, holdPointRef: 'HP-COM-02', holdPointDescription: 'TMR inspector witness and sign-off on completed SCATS functional test',
        requiresPhoto: false,
        asBuiltField: 'TMR inspector name: ___  Signature: ___  Date/time: ___',
      },
    ],
    estimatedDuration: '4–8 hours (full intersection)',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-04-15T10:00:00Z',
    createdAt: '2026-04-10T09:00:00Z', updatedAt: '2026-04-15T10:00:00Z',
  },

  {
    id: 'wi-sop-012',
    reference: 'WI-SOP-HV-002',
    title: 'HV switching programme — parallel isolation procedure',
    version: '3.0',
    type: 'SOP',
    discipline: 'HV_ELECTRICAL',
    status: 'APPROVED',
    applicableRegulations: ['Energex HV Switching Code Rev 6', 'Electrical Safety Act 2002 (QLD)', 'AS/NZS 4836 — Safe work on LV electrical installations'],
    requiredCredentials: ['HV Authorised Person (Energex AP)', 'Switching Permit Issuer accreditation (Energex)', 'Electrical licence (EL2 or above)'],
    requiredTools: ['Approved HV voltage indicator (Class 1)', 'Earthing equipment (rated for system voltage)', 'Locking/tagging equipment'],
    ppeRequirements: ['Arc flash PPE (cal/cm² rated for system level)', 'HV insulating gloves (Class 2)', 'HV face shield', 'Insulating matting', 'Hard hat'],
    swmsReference: 'SWMS-HV-SWITCHING-001',
    steps: [
      {
        number: 1, title: 'Obtain authorised HV Switching Programme',
        instruction: 'Obtain written and authorised HVSP from Energex System Control. Confirm HVSP matches the intended isolation. Do not commence any HV switching from memory — HVSP must be in hand.',
        isHoldPoint: true, holdPointRef: 'HP-HV-SW-01', holdPointDescription: 'HVSP in hand and confirmed with Energex System Control before any switching commences',
        requiresPhoto: false,
        asBuiltField: 'HVSP number: ___  Issued by: ___  Date/time: ___',
      },
      {
        number: 2, title: 'Execute switching steps per HVSP',
        instruction: 'Execute switching steps in HVSP sequence only. Tick and initial each step as completed. No deviation from HVSP sequence is permitted. If a step cannot be completed as written, STOP and contact Energex System Control.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 3, title: 'Prove dead — test before touch',
        instruction: 'Using approved HV voltage indicator, prove the isolated cable/equipment is dead at all approach points. Test on known live source first to verify instrument function. Do not rely on switching records alone.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'HV voltage indicator in use on isolated cable — reading visible',
        qualityCheck: 'Test on known live source before and after to confirm instrument function.',
      },
      {
        number: 4, title: 'Apply earths — record positions',
        instruction: 'Apply rated earth clamps at all specified earthing points per HVSP. Record earth positions on HVSP diagram. No work is to commence on any HV cable or equipment until earths are applied and confirmed at all points.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Earth clamps applied — clearly visible on cable at earthing point',
        asBuiltField: 'Earth positions applied: ___  Applied by: ___  Time: ___',
      },
    ],
    estimatedDuration: '2–4 hours (switching and earthing only)',
    createdById: 'u2', createdByName: 'Jane Smith',
    authorisedById: 'u2', authorisedByName: 'Jane Smith', authorisedAt: '2026-01-20T11:00:00Z',
    createdAt: '2026-01-15T09:00:00Z', updatedAt: '2026-01-20T11:00:00Z',
  },

  // Draft SOP — not yet approved
  {
    id: 'wi-sop-013',
    reference: 'WI-SOP-COM-002',
    title: 'Fibre optic splice — OTDR test and documentation',
    version: '0.1',
    type: 'SOP',
    discipline: 'COMMISSIONING',
    status: 'DRAFT',
    applicableRegulations: ['AS/NZS 3080 — Telecommunications installations', 'TMR ITS Fibre Standards'],
    requiredCredentials: ['Fibre optic splicer competency', 'Electrical licence (EL1 or above)'],
    requiredTools: ['Fusion splicer', 'OTDR unit', 'Fibre cleaver', 'Optical power meter'],
    ppeRequirements: ['Safety glasses (fibre disposal hazard)', 'Hi-vis Class D', 'Steel-capped boots'],
    steps: [
      {
        number: 1, title: 'Prepare fibre ends for splicing',
        instruction: 'Strip fibre coating to required length. Clean using appropriate IPA wipe. Cleave fibre using calibrated cleaver — minimum cleave angle <0.5°. Inspect end face under microscope.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 2, title: 'Fusion splice and test at splice point',
        instruction: 'Insert fibres into fusion splicer. Run auto-splice. Confirm splice loss reading on splicer display — maximum 0.1dB for single-mode. Protect splice in approved sleeve and heat-shrink.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Fusion splicer display showing splice loss result',
        asBuiltField: 'Splice loss (dB): ___',
      },
      {
        number: 3, title: 'OTDR trace — full link test',
        instruction: 'Conduct OTDR test from each end of the installed fibre run. Record trace and confirm all splice points and connectors are within specification. Save OTDR trace files with job reference and fibre ID.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'OTDR display showing full link trace with loss values',
        asBuiltField: 'OTDR total link loss (dB): ___  Within spec: YES / NO',
      },
    ],
    estimatedDuration: '1 hour per splice / 4 hours full-link OTDR',
    createdById: 'u5', createdByName: 'Mike Walsh',
    createdAt: '2026-06-15T09:00:00Z', updatedAt: '2026-06-15T09:00:00Z',
  },
]

// ── Job-Specific Work Packages ────────────────────────────────────

export const MOCK_JOB_PACKAGES: WorkInstruction[] = [
  {
    id: 'wi-pkg-001',
    reference: 'WI-TP001-T1-01',
    title: 'Install NW corner signal pole foundation — TP-2026-001 Task 1.1.1',
    version: '1.0',
    type: 'JOB_PACKAGE',
    discipline: 'CIVIL',
    status: 'SUBMITTED',
    applicableJobIds: ['j1'],
    drawingTaskId: 'tsk003',             // DrawingTask FK — Phase 1
    taskReference: 'TP-2026-001 Task 1.1.1',
    drawingReference: 'J1-C02 Sheet 2 Detail A',
    specificationClause: 'MRTS50 cl. 6.3.4',
    siteSpecificNotes: 'Gas main confirmed at 750mm depth per RFI-TP001-001. Install standoff sleeve. Do NOT excavate below 650mm without PM authorisation. Concrete supply: Boral Pinkenba plant — order number BCQ-2026-441.',
    baseSopId: 'wi-sop-001',
    applicableRegulations: ['MRTS50 cl. 6.3.4', 'AS 3600 — Concrete structures', 'RFI-TP001-001 (gas main standoff requirement)'],
    requiredCredentials: ['Construction Induction (White Card)', 'Excavation spotter'],
    requiredTools: ['Excavator (300mm bucket)', 'Spirit level (1.2m min)', 'Laser level', 'Vibrating plate compactor', 'Nuclear density gauge'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Gloves'],
    swmsReference: 'SWMS-TP001-003',
    steps: [
      {
        number: 1, title: 'Confirm location and services check — NW corner',
        instruction: 'Verify survey pegs at NW corner location. Confirm DBYD clearance for TP-2026-001. CAT sweep the excavation area. NOTE: Gas main confirmed at 750mm depth per RFI-TP001-001 — do NOT excavate below 650mm.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'NW corner location before excavation — survey peg visible',
        completedAt: '2026-06-18T07:45:00Z', completedById: 'e1',
      },
      {
        number: 2, title: 'Excavate to 650mm depth (gas main limit)',
        instruction: 'Excavate to 650mm maximum depth (gas main standoff per RFI-TP001-001). Diameter per Drawing J1-C02 Sheet 2 Detail A. Install standoff sleeve before base plate. Confirm sleeve position with PM before proceeding.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Excavation at 650mm with tape measure; gas standoff sleeve installed',
        completedAt: '2026-06-18T09:30:00Z', completedById: 'e1',
      },
      {
        number: 3, title: 'HOLD POINT HP-001 — PC inspection at depth',
        instruction: 'STOP. PC Sarah Lee to inspect excavation depth, standoff sleeve position, and confirm OK to proceed before base plate installation.',
        isHoldPoint: true, holdPointRef: 'HP-001', holdPointDescription: 'PC inspection — depth, standoff sleeve position, drawing compliance',
        requiresPhoto: false,
        completedAt: '2026-06-18T10:15:00Z', completedById: 'u4',
      },
      {
        number: 4, title: 'Install base plate and set to level',
        instruction: 'Position DIN base plate per Drawing J1-C02 Detail A. Bolt orientation per drawing compass. Level ±5mm in 1m.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Base plate in position with spirit level on plate; bolt orientation alignment visible',
        completedAt: '2026-06-18T11:00:00Z', completedById: 'e1',
        asBuiltField: 'Bolt projection (mm): 195  Level check: PASS',
      },
      {
        number: 5, title: 'Record concrete batch — Boral Pinkenba',
        instruction: 'Confirm Boral order BCQ-2026-441. Accept only Grade 40, 20mm aggregate, slump 70–100mm. Photograph docket.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Boral delivery docket showing batch no. and specification',
        completedAt: '2026-06-18T13:00:00Z', completedById: 'e1',
        asBuiltField: 'Batch no: B24-20261803  Truck: BRL-044  Delivery: 13:05',
      },
      {
        number: 6, title: 'Pour Grade 40 concrete',
        instruction: 'Pour per SOP-CIV-001 Step 6. Do not vibrate within 200mm of base plate.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Pour in progress; completed pour with bolt projection',
        completedAt: '2026-06-18T14:00:00Z', completedById: 'e1',
      },
      {
        number: 7, title: 'Backfill and compact — gas main clearance maintained',
        instruction: 'Backfill around foundation in 200mm lifts. Hand compact within 300mm of gas main depth. Vibrating plate above 350mm only. Achieve ≥95% Standard Proctor. Reinstate footpath.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Completed and reinstated foundation from above; footpath surface condition',
        completedAt: '2026-06-19T08:30:00Z', completedById: 'e1',
        asBuiltField: 'Compaction L1: 97% L2: 96% L3: 95%',
      },
    ],
    estimatedDuration: '1 day',
    authorisedById: 'u4', authorisedByName: 'Sarah Lee', authorisedAt: '2026-06-16T09:00:00Z',
    assignedToId: 'e1', assignedToName: 'Tony Rivera',
    deployedAt: '2026-06-17T17:00:00Z',
    completedById: 'e1', completedByName: 'Tony Rivera', completedAt: '2026-06-19T09:00:00Z',
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2026-06-14T10:00:00Z', updatedAt: '2026-06-19T09:00:00Z',
    notes: 'Gas main depth per RFI-TP001-001 confirmed. PC to review and sign off.',
  },

  {
    id: 'wi-pkg-002',
    reference: 'WI-TP001-T2-03',
    title: 'Cable pulling circuits C1–C4 — TP-2026-001 Task 2.3',
    version: '1.0',
    type: 'JOB_PACKAGE',
    discipline: 'ELECTRICAL',
    status: 'IN_PROGRESS',
    applicableJobIds: ['j1'],
    drawingTaskId: 'tsk011',             // DrawingTask FK — Phase 1
    taskReference: 'TP-2026-001 Task 2.3',
    drawingReference: 'J1-E04 Cable Schedule Rev B',
    specificationClause: 'AS/NZS 3000 cl. 3.4',
    siteSpecificNotes: 'Circuits C1–C4 are the primary supply from Pit 1 to Pit 7. Orange HD conduit run 85m. Olex cable order confirmed — delivery docket with batch no. must be photographed before breaking seal. All terminations at Pit 7 to be left with 2m service loop for cabinet connection in next phase.',
    baseSopId: 'wi-sop-003',
    applicableRegulations: ['AS/NZS 3000 — Wiring Rules'],
    requiredCredentials: ['Electrical licence (EL1 or above)', 'Construction Induction (White Card)'],
    requiredTools: ['Cable pulling winch', 'Cable drum stand with brake', 'Cable tension gauge', 'Circuit labelling tape'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Safety glasses', 'Cut-resistant gloves'],
    steps: [
      {
        number: 1, title: 'Photograph Olex cable drum labels — all 4 circuits',
        instruction: 'Before breaking seal on each Olex drum, photograph the label showing: cable type, cross-section, batch number, drum number, AS/NZS certification mark. Record details.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 4, photoRequirement: 'Each of the 4 drum labels — batch number and AS/NZS cert mark legible',
        completedAt: '2026-06-20T07:30:00Z', completedById: 'e1',
        asBuiltField: 'C1 batch: OX-24-168  C2 batch: OX-24-168  C3 batch: OX-24-169  C4 batch: OX-24-169',
      },
      {
        number: 2, title: 'Inspect conduit run Pit 1 to Pit 7',
        instruction: 'Draw fish tape through entire orange HD conduit run (85m). Confirm no obstruction. Do not force — report any resistance to PM immediately. Confirm conduit fill ratio within AS/NZS 3000 limits.',
        isHoldPoint: false, requiresPhoto: false,
        completedAt: '2026-06-20T08:15:00Z', completedById: 'e1',
      },
      {
        number: 3, title: 'Pull circuit C1',
        instruction: 'Pull C1 cable from Pit 1 to Pit 7. Monitor tension — record maximum. Leave 2m service loop at Pit 7. Label both ends C1 before coiling.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'C1 pull in progress (cable entering conduit); C1 service loop at Pit 7 with label visible',
        completedAt: '2026-06-20T10:00:00Z', completedById: 'e1',
        asBuiltField: 'C1 max tension: 1.4 kN  Run length: 85m',
      },
      {
        number: 4, title: 'Pull circuit C2',
        instruction: 'As per C1. Label C2.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'C2 service loop at Pit 7 with circuit label visible',
        completedAt: null, completedById: null,
      },
      {
        number: 5, title: 'Pull circuits C3 and C4',
        instruction: 'As per C1. Label C3 and C4.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'C3 and C4 at Pit 7 with labels; all 4 circuits coiled and secured in pit',
        completedAt: null, completedById: null,
      },
      {
        number: 6, title: 'Seal conduit entries and photograph completed pit',
        instruction: 'Seal all conduit entries at Pit 1 and Pit 7 with conduit sealant plugs. Label each circuit at both pits. Photograph complete arrangement at each end.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 4, photoRequirement: 'Sealed conduit entries at Pit 1 (2 photos); all 4 labelled service loops at Pit 7 (2 photos)',
        completedAt: null, completedById: null,
      },
    ],
    estimatedDuration: '1 day',
    authorisedById: 'u4', authorisedByName: 'Sarah Lee', authorisedAt: '2026-06-18T16:00:00Z',
    assignedToId: 'e1', assignedToName: 'Tony Rivera',
    deployedAt: '2026-06-19T17:00:00Z',
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2026-06-16T14:00:00Z', updatedAt: '2026-06-20T10:00:00Z',
  },

  {
    id: 'wi-pkg-003',
    reference: 'WI-TP001-CS-01',
    title: 'Confined space entry — Pit 4B cable connection — TP-2026-001',
    version: '1.0',
    type: 'JOB_PACKAGE',
    discipline: 'ALL',
    status: 'APPROVED',
    applicableJobIds: ['j1'],
    drawingTaskId: 'tsk012',             // DrawingTask FK — Phase 1
    taskReference: 'TP-2026-001 Task 3.2',
    drawingReference: 'J1-E04 Pit 4B Detail',
    specificationClause: 'WHS Regulation 2011 Part 6.5',
    siteSpecificNotes: 'Pit 4B is located in a low-lying area near the drainage channel. History of water ingress after rain — pump out if any standing water before entry. Entry permit to be obtained from WHSO before each entry — not transferable between shifts.',
    baseSopId: 'wi-sop-009',
    applicableRegulations: ['WHS Regulation 2011 Part 6.5', 'Code of Practice: Confined Spaces', 'AS 2865'],
    requiredCredentials: ['Confined Space Entry (worker)', 'Confined Space Entry Supervisor', 'Rescue team trained standby person'],
    requiredTools: ['4-gas atmospheric tester (calibrated)', 'Tripod and winch', 'SCBA (standby only)', 'Full body harness and rescue lanyard', 'Submersible pump (if standing water)'],
    ppeRequirements: ['Hard hat', 'Hi-vis Class D', 'Steel-capped boots', 'Chemical-resistant gloves', 'Full body harness'],
    swmsReference: 'SWMS-TP001-CS-001',
    steps: [
      {
        number: 1, title: 'Complete entry permit — note Pit 4B water ingress risk',
        instruction: 'Complete Form WHSO-CS-001 for Pit 4B. Note water ingress risk in hazard assessment. Check for standing water before permit is signed. Pump out if required.',
        isHoldPoint: false, requiresPhoto: false,
        asBuiltField: 'Permit no: ___  Supervisor: ___  Time opened: ___  Standing water: YES / NO',
      },
      {
        number: 2, title: 'Atmospheric test — all four gases',
        instruction: 'Test at top, mid, and bottom of Pit 4B. Additional test required if any standing water was present and pumped out (H2S risk from disturbed sediment). Record all readings.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Gas meter reading in Pit 4B showing all four gas values',
        qualityCheck: 'O2: 19.5–23.5%. LEL <5%. CO <25ppm. H2S <10ppm.',
        asBuiltField: 'O2: ___ LEL: ___ CO: ___ H2S: ___',
      },
      {
        number: 3, title: 'HOLD POINT — supervisor sign-off',
        instruction: 'STOP. WHSO or nominated supervisor must review atmospheric results and sign permit before entry.',
        isHoldPoint: true, holdPointRef: 'HP-CS-01', holdPointDescription: 'Atmospheric test supervisor sign-off',
        requiresPhoto: false,
      },
      {
        number: 4, title: 'Set up tripod and conduct entry',
        instruction: 'Set up tripod. Entrant in harness, lanyard connected before entry. Standby in position. Conduct cable connection works per Electrical SOP. Monitor atmosphere throughout.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Tripod set up at Pit 4B; standby person visible',
      },
      {
        number: 5, title: 'Exit and close permit',
        instruction: 'All persons exit. Tally count confirmed. Replace pit lid. Close permit.',
        isHoldPoint: false, requiresPhoto: false,
        asBuiltField: 'Exit time: ___  Permit closed by: ___',
      },
    ],
    estimatedDuration: '3 hours',
    authorisedById: 'u4', authorisedByName: 'Sarah Lee', authorisedAt: '2026-06-20T09:00:00Z',
    assignedToId: null, assignedToName: null,
    deployedAt: null,
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2026-06-18T14:00:00Z', updatedAt: '2026-06-20T09:00:00Z',
  },

  {
    id: 'wi-pkg-004',
    reference: 'WI-TP003-T1-01',
    title: 'Traffic management setup — Maroochydore Rd — TP-2026-003 Task 1.1',
    version: '1.0',
    type: 'JOB_PACKAGE',
    discipline: 'TRAFFIC_CONTROL',
    status: 'DRAFT',
    applicableJobIds: ['j3'],
    taskReference: 'TP-2026-003 Task 1.1',
    drawingReference: 'J3-TMP-001 Rev A',
    specificationClause: 'TMR TMAP Requirements — Single-lane alternating',
    siteSpecificNotes: 'Maroochydore Rd: 80kph posted speed. Peak hour restriction — TCP must be in position by 06:00 and withdrawn by 08:30 and 15:30–17:30. Minimum 2 controllers required at all times. Refer TMP for advance warning sign locations (school zone active Mon–Fri).',
    baseSopId: 'wi-sop-008',
    applicableRegulations: ['AS 1742.3', 'MUTCD Part 3', 'TMR TMAP Requirements'],
    requiredCredentials: ['Traffic Controller licence (Queensland)', 'Traffic Management Implementer (supervisor)'],
    requiredTools: ['Stop/Slow bats (2)', 'Site radios (2)', 'Portable warning signs', 'Delineator cones (min 20)'],
    ppeRequirements: ['Hi-vis Class D — mandatory', 'Hard hat', 'Steel-capped boots'],
    steps: [
      {
        number: 1, title: 'Confirm TMP-001 Rev A before setup',
        instruction: 'Read J3-TMP-001 Rev A before commencing. Confirm school zone sign locations and peak hour restrictions. All TCP arrangements must match TMP exactly.',
        isHoldPoint: false, requiresPhoto: false,
      },
      {
        number: 2, title: 'Erect advance warning signage by 05:45',
        instruction: 'All advance warning signs per TMP must be in position before first TCP hour (06:00). 80kph zone — minimum sight distances per AS 1742.3 Table B1.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 2, photoRequirement: 'Both approach advance warning sign setups at 06:00 before first vehicle release',
      },
      {
        number: 3, title: 'Confirm radio comms and open TCP',
        instruction: 'Both controllers confirm radio contact. Open TCP as per TMP diagram. Do not release vehicles until opposite controller confirms STOP.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Both TCP positions occupied — site condition at traffic opening',
      },
      {
        number: 4, title: 'End of session — reinstate and sign off',
        instruction: 'Remove all TCP signing and delineators at end of permitted window. Photograph reinstated road. Log TCP hours on site diary.',
        isHoldPoint: false, requiresPhoto: true, photoCount: 1, photoRequirement: 'Road fully reinstated at end of TCP session',
      },
    ],
    estimatedDuration: 'Duration of daily works',
    createdById: 'u6', createdByName: 'Mike Walsh',
    createdAt: '2026-06-20T11:00:00Z', updatedAt: '2026-06-20T11:00:00Z',
  },
]

// ── Helpers ───────────────────────────────────────────────────────

export const ALL_WORK_INSTRUCTIONS: WorkInstruction[] = [...MOCK_SOPS, ...MOCK_JOB_PACKAGES]

export function getWIsByJob(jobId: string): WorkInstruction[] {
  return MOCK_JOB_PACKAGES.filter(wi => wi.applicableJobIds?.includes(jobId))
}

export function getStepCompletionCount(wi: WorkInstruction): { done: number; total: number } {
  const nonHold = wi.steps.filter(s => !s.isHoldPoint)
  return {
    done: nonHold.filter(s => s.completedAt).length,
    total: nonHold.length,
  }
}

export function getHoldPoints(wi: WorkInstruction) {
  return wi.steps.filter(s => s.isHoldPoint)
}

export function getTotalPhotoRequirements(wi: WorkInstruction): number {
  return wi.steps.reduce((sum, s) => sum + (s.requiresPhoto ? (s.photoCount ?? 1) : 0), 0)
}
